// RadarChart component - SVG radar chart with render modes
import * as styles from './RadarChart.css';
import {
  RadarChartVM,
  RadarPoint,
  getAxisLabelPosition,
} from '../../view-model/RadarChart.viewModel';

export type RadarChartProps = {
  vm: RadarChartVM;
  className?: string;
};

const POINT_RADIUS = 6;
const LABEL_OFFSET = 30;

const createPolygonPath = (points: RadarPoint[]): string => {
  if (points.length === 0) return '';

  const pathParts = points.map((point, index) =>
    index === 0 ? `M ${point.x} ${point.y}` : `L ${point.x} ${point.y}`,
  );

  return `${pathParts.join(' ')} Z`;
};

export const RadarChart = ({ vm, className }: RadarChartProps) => {
  const { axes, gridLines, axisLines, center, maxRadius, size, mode } = vm;

  return (
    <div
      className={
        className ? `${styles.container} ${className}` : styles.container
      }
      data-testid="radar-chart"
    >
      <svg
        className={styles.svg}
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
      >
        {gridLines.map((pathD, index) => (
          <path
            key={`grid-${index}`}
            d={pathD}
            className={styles.gridLine}
            data-testid={`grid-line-${index}`}
          />
        ))}

        {axisLines.map((line) => (
          <line
            key={`axis-line-${line.end.categoryId}`}
            x1={line.start.x}
            y1={line.start.y}
            x2={line.end.x}
            y2={line.end.y}
            className={styles.axisLine}
            data-testid={`axis-line-${line.end.categoryId}`}
          />
        ))}

        {mode.type === 'single-point' && (
          <circle
            cx={mode.point.x}
            cy={mode.point.y}
            r={POINT_RADIUS}
            className={styles.dataPoint}
            data-testid="data-point"
          />
        )}

        {mode.type === 'line' && (
          <>
            <line
              x1={mode.points[0].x}
              y1={mode.points[0].y}
              x2={mode.points[1].x}
              y2={mode.points[1].y}
              className={styles.dataLine}
              data-testid="data-line"
            />
            {mode.points.map((point) => (
              <circle
                key={point.categoryId}
                cx={point.x}
                cy={point.y}
                r={POINT_RADIUS}
                className={styles.dataPoint}
                data-testid="data-point"
              />
            ))}
          </>
        )}

        {mode.type === 'polygon' && (
          <>
            <path
              d={createPolygonPath(mode.points)}
              className={styles.dataPolygon}
              data-testid="data-polygon"
            />
            {mode.points.map((point) => (
              <circle
                key={point.categoryId}
                cx={point.x}
                cy={point.y}
                r={POINT_RADIUS}
                className={styles.dataPoint}
                data-testid="data-point"
              />
            ))}
          </>
        )}

        {axes.map((axis) => {
          const labelPos = getAxisLabelPosition(
            axis,
            center,
            maxRadius,
            LABEL_OFFSET,
          );
          const labelClassName = axis.isHighlighted
            ? `${styles.label} ${styles.labelHighlighted}`
            : styles.label;

          return (
            <text
              key={axis.id}
              x={labelPos.x}
              y={labelPos.y}
              className={labelClassName}
              data-testid={`axis-label-${axis.id}`}
            >
              {axis.name}
            </text>
          );
        })}
      </svg>
    </div>
  );
};
